import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useScrollLock } from '../hooks/useScrollLock';
import {
  getAdminUsers,
  getAdminDashboardStats,
  getExerciseTemplatesAdmin,
  createExerciseTemplate,
  updateExerciseTemplate,
  deleteExerciseTemplate,
  updateUserProfile,
  getExerciseRequests,
  approveExerciseRequest,
  rejectExerciseRequest,
  findSimilarExercises
} from '../lib/database';
import SkeletonLoader from './SkeletonLoader';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const { signOut, profile } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [exerciseSubTab, setExerciseSubTab] = useState('catalog');
  const [loading, setLoading] = useState(true);

  // Data states
  const [stats, setStats] = useState({ totalAthletes: 0, totalCoaches: 0, totalPrograms: 0, totalWorkouts: 0 });
  const [users, setUsers] = useState([]); // lista unica utenti (RPC admin_list_users)
  const [userFilter, setUserFilter] = useState('all');
  const [exercises, setExercises] = useState([]);
  const [exerciseRequests, setExerciseRequests] = useState([]);

  // Exercise request approval state
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [approvalFormData, setApprovalFormData] = useState({
    name: '',
    primary_muscle_group: '',
    description: '',
    muscles_involved: '',
    execution_tips: '',
    common_mistakes: '',
    media_url: '',
    media_type: 'gif',
    exercise_type: 'reps',
    increment_step: ''
  });
  const [savingApproval, setSavingApproval] = useState(false);
  const [approvalDuplicates, setApprovalDuplicates] = useState([]);

  // Exercise form state
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [exerciseFormData, setExerciseFormData] = useState({
    name: '',
    primary_muscle_group: '',
    description: '',
    muscles_involved: '',
    execution_tips: '',
    common_mistakes: '',
    media_url: '',
    media_type: 'gif'
  });
  const [saving, setSaving] = useState(false);

  // Search states
  const [userSearch, setUserSearch] = useState('');
  const [exerciseSearch, setExerciseSearch] = useState('');

  // User edit state
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({ name: '', email: '', role: '' });
  const [savingUser, setSavingUser] = useState(false);

  useScrollLock(showExerciseForm || showUserForm || !!approvingRequest);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [statsData, usersData, exercisesData, requestsData] = await Promise.all([
        getAdminDashboardStats(),
        getAdminUsers(),
        getExerciseTemplatesAdmin(),
        getExerciseRequests()
      ]);

      setStats(statsData);
      setUsers(usersData);
      setExercises(exercisesData);
      setExerciseRequests(requestsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingRequestsCount = exerciseRequests.filter(r => r.status === 'pending').length;

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  // Exercise CRUD
  const resetExerciseForm = () => {
    setExerciseFormData({
      name: '',
      primary_muscle_group: '',
      description: '',
      muscles_involved: '',
      execution_tips: '',
      common_mistakes: '',
      media_url: '',
      media_type: 'gif'
    });
    setEditingExercise(null);
  };

  const handleEditExercise = (exercise) => {
    setExerciseFormData({
      name: exercise.name || '',
      primary_muscle_group: exercise.primary_muscle_group || '',
      description: exercise.description || '',
      muscles_involved: exercise.muscles_involved || '',
      execution_tips: exercise.execution_tips || '',
      common_mistakes: exercise.common_mistakes || '',
      media_url: exercise.media_url || '',
      media_type: exercise.media_type || 'gif'
    });
    setEditingExercise(exercise);
    setShowExerciseForm(true);
  };

  const handleExerciseSubmit = async (e) => {
    e.preventDefault();
    if (!exerciseFormData.name.trim() || !exerciseFormData.primary_muscle_group.trim()) return;

    try {
      setSaving(true);
      if (editingExercise) {
        await updateExerciseTemplate(editingExercise.id, exerciseFormData);
      } else {
        await createExerciseTemplate(exerciseFormData);
      }
      await loadAllData();
      setShowExerciseForm(false);
      resetExerciseForm();
      toast.success('Esercizio salvato con successo');
    } catch (error) {
      console.error('Error saving exercise:', error);
      toast.error('Errore nel salvataggio: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExercise = async (exercise) => {
    if (!window.confirm(`Eliminare "${exercise.name}"?`)) return;
    try {
      await deleteExerciseTemplate(exercise.id);
      await loadAllData();
      toast.success('Esercizio eliminato');
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Exercise Requests
  const handleOpenApproval = async (request) => {
    setApprovingRequest(request);
    setApprovalDuplicates([]);
    setApprovalFormData({
      name: request.requested_name || '',
      primary_muscle_group: request.suggested_muscle_group || '',
      description: '',
      muscles_involved: '',
      execution_tips: '',
      common_mistakes: '',
      media_url: '',
      media_type: 'gif',
      exercise_type: 'reps',
      increment_step: ''
    });
    // Check for similar exercises already in catalog
    if (request.requested_name) {
      const similar = await findSimilarExercises(request.requested_name);
      setApprovalDuplicates(similar);
    }
  };

  const handleApproveRequest = async (e) => {
    e.preventDefault();
    if (!approvalFormData.name.trim() || !approvalFormData.primary_muscle_group.trim()) return;
    try {
      setSavingApproval(true);
      const templateData = {
        ...approvalFormData,
        increment_step: approvalFormData.increment_step ? parseFloat(approvalFormData.increment_step) : null
      };
      await approveExerciseRequest(approvingRequest.id, templateData, profile.id);
      await loadAllData();
      setApprovingRequest(null);
      toast.success('Esercizio approvato e aggiunto al catalogo');
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Errore nell\'approvazione: ' + error.message);
    } finally {
      setSavingApproval(false);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm('Rifiutare questa richiesta?')) return;
    try {
      await rejectExerciseRequest(requestId, profile.id);
      await loadAllData();
      toast.success('Richiesta rifiutata');
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Errore nel rifiuto: ' + error.message);
    }
  };

  // User CRUD
  const handleEditUser = (user) => {
    setUserFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'athlete'
    });
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!userFormData.name.trim()) return;

    try {
      setSavingUser(true);
      await updateUserProfile(editingUser.id, userFormData);
      await loadAllData();
      setShowUserForm(false);
      setEditingUser(null);
      toast.success('Utente aggiornato');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Errore nel salvataggio: ' + error.message);
    } finally {
      setSavingUser(false);
    }
  };

  // Classificazione utente (capability, non ruolo):
  //  admin | coach_active | coach_inactive (con storico) | athlete
  const classifyUser = (u) => {
    if (u.role === 'admin') return 'admin';
    if (u.coach_mode) return 'coach_active';
    if ((u.athletes_count || 0) > 0 || (u.coach_programs_count || 0) > 0) return 'coach_inactive';
    return 'athlete';
  };

  const userCounts = {
    all: users.length,
    coach_active: users.filter(u => classifyUser(u) === 'coach_active').length,
    coach_inactive: users.filter(u => classifyUser(u) === 'coach_inactive').length,
    athlete: users.filter(u => classifyUser(u) === 'athlete').length,
    admin: users.filter(u => classifyUser(u) === 'admin').length,
  };

  const filteredUsers = users
    .filter(u => userFilter === 'all' || classifyUser(u) === userFilter)
    .filter(u =>
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase())
    );

  const USER_FILTERS = [
    { key: 'all', label: 'Tutti gli utenti' },
    { key: 'coach_active', label: 'Coach attivi' },
    { key: 'coach_inactive', label: 'Coach non attivi con storico' },
    { key: 'athlete', label: 'Solo atleti' },
    { key: 'admin', label: 'Admin' },
  ];

  const filteredExercises = exercises.filter(e =>
    e.name?.toLowerCase().includes(exerciseSearch.toLowerCase()) ||
    e.primary_muscle_group?.toLowerCase().includes(exerciseSearch.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
        </div>
        <div className="admin-content">
          <SkeletonLoader variant="list" count={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1 className="admin-title">Back Office</h1>
            <p className="admin-subtitle">Ciao, {profile?.name}</p>
          </div>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Panoramica
        </button>
        <button
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Utenti ({users.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'exercises' ? 'active' : ''}`}
          onClick={() => setActiveTab('exercises')}
        >
          Esercizi ({exercises.length})
          {pendingRequestsCount > 0 && (
            <span className="admin-tab-badge">{pendingRequestsCount}</span>
          )}
        </button>
      </div>

      {/* Content */}
      <main className="admin-main">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="admin-overview">
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <div className="admin-stat-icon">👥</div>
                <div className="admin-stat-value">{users.length}</div>
                <div className="admin-stat-label">Utenti</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-icon">🏋️</div>
                <div className="admin-stat-value">{userCounts.coach_active}</div>
                <div className="admin-stat-label">Coach attivi</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-icon">📋</div>
                <div className="admin-stat-value">{stats.totalPrograms}</div>
                <div className="admin-stat-label">Programmi</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-icon">✅</div>
                <div className="admin-stat-value">{stats.totalWorkouts}</div>
                <div className="admin-stat-label">Allenamenti</div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab (lista unica) */}
        {activeTab === 'users' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <input
                type="text"
                placeholder="Cerca per nome o email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="admin-search"
              />
            </div>

            {/* Filtri per capability */}
            <div className="admin-user-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '4px 0 12px' }}>
              {USER_FILTERS.map(f => (
                <button
                  key={f.key}
                  onClick={() => setUserFilter(f.key)}
                  style={{
                    padding: '6px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
                    border: '1px solid ' + (userFilter === f.key ? '#667eea' : '#e5e7eb'),
                    background: userFilter === f.key ? '#667eea' : 'white',
                    color: userFilter === f.key ? 'white' : '#374151', cursor: 'pointer'
                  }}
                >
                  {f.label} ({userCounts[f.key === 'athlete' ? 'athlete' : f.key]})
                </button>
              ))}
            </div>

            {filteredUsers.length === 0 ? (
              <div className="admin-empty"><p>Nessun utente trovato</p></div>
            ) : (
              <div className="admin-list">
                {filteredUsers.map(u => {
                  const type = classifyUser(u);
                  const typeBadge = {
                    admin: { t: 'Admin', bg: '#fee2e2', c: '#991b1b' },
                    coach_active: { t: 'Coach attivo', bg: '#dcfce7', c: '#166534' },
                    coach_inactive: { t: 'Coach (storico)', bg: '#fef3c7', c: '#92400e' },
                    athlete: { t: 'Atleta', bg: '#e0e7ff', c: '#3730a3' },
                  }[type];
                  return (
                    <div key={u.id} className="admin-list-item">
                      <div className="admin-list-avatar">{type === 'admin' ? '🛡️' : type.startsWith('coach') ? '🏋️' : '👤'}</div>
                      <div className="admin-list-info">
                        <div className="admin-list-name">
                          {u.name || 'Senza nome'}
                          <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: typeBadge.bg, color: typeBadge.c }}>
                            {typeBadge.t}
                          </span>
                        </div>
                        <div className="admin-list-email">{u.email || '-'}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                          Modalità Coach: <strong>{u.coach_mode ? 'ON' : 'OFF'}</strong>
                          {' · '}Atleti seguiti: <strong>{u.athletes_count || 0}</strong>
                          {' · '}Programmi creati: <strong>{u.coach_programs_count || 0}</strong>
                        </div>
                      </div>
                      <div className="admin-list-meta">
                        <span className="admin-list-date">Registrato: {formatDate(u.created_at)}</span>
                        <button className="admin-edit-user-btn" onClick={() => handleEditUser(u)} title="Modifica">✏️</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Exercises Tab */}
        {activeTab === 'exercises' && (
          <div className="admin-section">
            {/* Sub-tabs */}
            <div className="admin-subtabs">
              <button
                className={`admin-subtab ${exerciseSubTab === 'catalog' ? 'active' : ''}`}
                onClick={() => setExerciseSubTab('catalog')}
              >
                Catalogo ({exercises.length})
              </button>
              <button
                className={`admin-subtab ${exerciseSubTab === 'requests' ? 'active' : ''}`}
                onClick={() => setExerciseSubTab('requests')}
              >
                Richieste
                {pendingRequestsCount > 0 && (
                  <span className="admin-tab-badge">{pendingRequestsCount}</span>
                )}
              </button>
              <button
                className={`admin-subtab ${exerciseSubTab === 'quality' ? 'active' : ''}`}
                onClick={() => setExerciseSubTab('quality')}
              >
                Qualità
              </button>
            </div>

            {/* Catalog sub-tab */}
            {exerciseSubTab === 'catalog' && (
              <>
                <div className="admin-section-header">
                  <input
                    type="text"
                    placeholder="Cerca esercizio..."
                    value={exerciseSearch}
                    onChange={(e) => setExerciseSearch(e.target.value)}
                    className="admin-search"
                  />
                  <button
                    className="admin-add-btn"
                    onClick={() => { resetExerciseForm(); setShowExerciseForm(true); }}
                  >
                    + Nuovo
                  </button>
                </div>
                {filteredExercises.length === 0 ? (
                  <div className="admin-empty">
                    <p>Nessun esercizio trovato</p>
                  </div>
                ) : (
                  <div className="admin-exercise-grid">
                    {filteredExercises.map(exercise => (
                      <div key={exercise.id} className="admin-exercise-card">
                        <div className="admin-exercise-header">
                          <h3 className="admin-exercise-name">{exercise.name}</h3>
                          <div className="admin-exercise-actions">
                            <button onClick={() => handleEditExercise(exercise)} title="Modifica">✏️</button>
                            <button onClick={() => handleDeleteExercise(exercise)} title="Elimina">🗑️</button>
                          </div>
                        </div>
                        <div className="admin-exercise-muscle">{exercise.primary_muscle_group}</div>
                        {exercise.description && (
                          <p className="admin-exercise-desc">{exercise.description.substring(0, 100)}...</p>
                        )}
                        <div className="admin-exercise-status">
                          <span className={exercise.description ? 'complete' : ''}>Desc</span>
                          <span className={exercise.execution_tips ? 'complete' : ''}>Tips</span>
                          <span className={exercise.media_url ? 'complete' : ''}>Media</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Requests sub-tab */}
            {exerciseSubTab === 'requests' && (
              <>
                {exerciseRequests.length === 0 ? (
                  <div className="admin-empty">
                    <p>Nessuna richiesta</p>
                  </div>
                ) : (
                  <div className="admin-list">
                    {exerciseRequests.map(request => (
                      <div key={request.id} className="admin-list-item admin-request-item">
                        <div className="admin-list-info" style={{ flex: 1 }}>
                          <div className="admin-list-name">{request.requested_name}</div>
                          <div className="admin-list-email">
                            {request.requesterName} • {request.requested_by_role || 'utente'}
                            {request.suggested_muscle_group && ` • ${request.suggested_muscle_group}`}
                          </div>
                          {request.note && (
                            <div className="admin-request-note">{request.note}</div>
                          )}
                          <div className="admin-list-date">{formatDate(request.created_at)}</div>
                        </div>
                        <div className="admin-request-status">
                          <span className={`admin-status-badge admin-status-${request.status}`}>
                            {request.status === 'pending' ? 'In attesa' : request.status === 'approved' ? 'Approvato' : 'Rifiutato'}
                          </span>
                          {request.status === 'pending' && (
                            <div className="admin-request-actions">
                              <button
                                className="admin-btn-approve"
                                onClick={() => handleOpenApproval(request)}
                              >
                                ✓ Approva
                              </button>
                              <button
                                className="admin-btn-reject"
                                onClick={() => handleRejectRequest(request.id)}
                              >
                                ✗ Rifiuta
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Quality sub-tab */}
            {exerciseSubTab === 'quality' && (
              <ExerciseQualityAudit exercises={exercises} onEdit={handleEditExercise} />
            )}
          </div>
        )}
      </main>

      {/* Exercise Form Modal */}
      {showExerciseForm && (
        <div className="admin-modal-overlay" onClick={() => setShowExerciseForm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingExercise ? 'Modifica Esercizio' : 'Nuovo Esercizio'}</h2>
              <button className="admin-modal-close" onClick={() => setShowExerciseForm(false)}>×</button>
            </div>
            <form onSubmit={handleExerciseSubmit} className="admin-modal-form">
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={exerciseFormData.name}
                    onChange={(e) => setExerciseFormData({ ...exerciseFormData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Gruppo Muscolare *</label>
                  <select
                    value={exerciseFormData.primary_muscle_group}
                    onChange={(e) => setExerciseFormData({ ...exerciseFormData, primary_muscle_group: e.target.value })}
                    required
                  >
                    <option value="">Seleziona gruppo muscolare...</option>
                    <option value="Petto">Petto</option>
                    <option value="Schiena">Schiena</option>
                    <option value="Spalle">Spalle</option>
                    <option value="Bicipiti">Bicipiti</option>
                    <option value="Tricipiti">Tricipiti</option>
                    <option value="Quadricipiti">Quadricipiti</option>
                    <option value="Femorali">Femorali</option>
                    <option value="Glutei">Glutei</option>
                    <option value="Polpacci">Polpacci</option>
                    <option value="Core">Core</option>
                    <option value="Avambracci">Avambracci</option>
                    <option value="Trapezio">Trapezio</option>
                    <option value="Cardio">Cardio</option>
                    <option value="Mobilità">Mobilità</option>
                  </select>
                </div>
              </div>

              <div className="admin-form-group">
                <label>Muscoli Coinvolti</label>
                <input
                  type="text"
                  value={exerciseFormData.muscles_involved}
                  onChange={(e) => setExerciseFormData({ ...exerciseFormData, muscles_involved: e.target.value })}
                  placeholder="Es. Pettorali, Tricipiti, Deltoidi"
                />
              </div>

              <div className="admin-form-group">
                <label>Descrizione</label>
                <textarea
                  value={exerciseFormData.description}
                  onChange={(e) => setExerciseFormData({ ...exerciseFormData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="admin-form-group">
                <label>Consigli Esecuzione</label>
                <textarea
                  value={exerciseFormData.execution_tips}
                  onChange={(e) => setExerciseFormData({ ...exerciseFormData, execution_tips: e.target.value })}
                  rows={3}
                  placeholder="Un consiglio per riga"
                />
              </div>

              <div className="admin-form-group">
                <label>Errori Comuni</label>
                <textarea
                  value={exerciseFormData.common_mistakes}
                  onChange={(e) => setExerciseFormData({ ...exerciseFormData, common_mistakes: e.target.value })}
                  rows={3}
                  placeholder="Un errore per riga"
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group" style={{ flex: 2 }}>
                  <label>URL Media (GIF/Immagine)</label>
                  <input
                    type="url"
                    value={exerciseFormData.media_url}
                    onChange={(e) => setExerciseFormData({ ...exerciseFormData, media_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="admin-form-group" style={{ flex: 1 }}>
                  <label>Tipo</label>
                  <select
                    value={exerciseFormData.media_type}
                    onChange={(e) => setExerciseFormData({ ...exerciseFormData, media_type: e.target.value })}
                  >
                    <option value="gif">GIF</option>
                    <option value="image">Immagine</option>
                    <option value="video">Video</option>
                  </select>
                </div>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowExerciseForm(false)}>
                  Annulla
                </button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Salvataggio...' : (editingExercise ? 'Salva' : 'Crea')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exercise Request Approval Modal */}
      {approvingRequest && (
        <div className="admin-modal-overlay" onClick={() => setApprovingRequest(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="admin-modal-header">
              <h2>Approva esercizio</h2>
              <button className="admin-modal-close" onClick={() => setApprovingRequest(null)}>×</button>
            </div>
            <div className="admin-approval-info">
              <p><strong>Proposto da:</strong> {approvingRequest.requesterName} ({approvingRequest.requested_by_role})</p>
              {approvingRequest.note && <p><strong>Nota:</strong> {approvingRequest.note}</p>}
            </div>
            {approvalDuplicates.length > 0 && (
              <div className="admin-duplicate-warning">
                <strong>Possibili duplicati nel catalogo:</strong>{' '}
                {approvalDuplicates.map(e => `${e.name} (${e.primary_muscle_group})`).join(' • ')}
                <br />
                <small>Verifica che non sia lo stesso esercizio prima di approvare.</small>
              </div>
            )}
            <form onSubmit={handleApproveRequest} className="admin-modal-form">
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={approvalFormData.name}
                    onChange={(e) => setApprovalFormData({ ...approvalFormData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Gruppo Muscolare *</label>
                  <select
                    value={approvalFormData.primary_muscle_group}
                    onChange={(e) => setApprovalFormData({ ...approvalFormData, primary_muscle_group: e.target.value })}
                    required
                  >
                    <option value="">Seleziona...</option>
                    {['Petto','Schiena','Spalle','Bicipiti','Tricipiti','Quadricipiti','Femorali','Glutei','Polpacci','Core','Avambracci','Trapezio','Cardio','Mobilità'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Tipo esercizio</label>
                  <select
                    value={approvalFormData.exercise_type}
                    onChange={(e) => setApprovalFormData({ ...approvalFormData, exercise_type: e.target.value })}
                  >
                    <option value="reps">Reps</option>
                    <option value="time">Tempo</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Incremento (kg)</label>
                  <select
                    value={approvalFormData.increment_step}
                    onChange={(e) => setApprovalFormData({ ...approvalFormData, increment_step: e.target.value })}
                  >
                    <option value="">Default (2.5)</option>
                    <option value="0.5">0.5 kg</option>
                    <option value="1">1 kg</option>
                    <option value="2.5">2.5 kg</option>
                    <option value="5">5 kg</option>
                  </select>
                </div>
              </div>

              <div className="admin-form-group">
                <label>Muscoli Coinvolti</label>
                <input
                  type="text"
                  value={approvalFormData.muscles_involved}
                  onChange={(e) => setApprovalFormData({ ...approvalFormData, muscles_involved: e.target.value })}
                  placeholder="Es. Pettorali, Tricipiti, Deltoidi"
                />
              </div>

              <div className="admin-form-group">
                <label>Descrizione</label>
                <textarea
                  value={approvalFormData.description}
                  onChange={(e) => setApprovalFormData({ ...approvalFormData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="admin-form-group">
                <label>Consigli Esecuzione</label>
                <textarea
                  value={approvalFormData.execution_tips}
                  onChange={(e) => setApprovalFormData({ ...approvalFormData, execution_tips: e.target.value })}
                  rows={3}
                  placeholder="Un consiglio per riga"
                />
              </div>

              <div className="admin-form-group">
                <label>Errori Comuni</label>
                <textarea
                  value={approvalFormData.common_mistakes}
                  onChange={(e) => setApprovalFormData({ ...approvalFormData, common_mistakes: e.target.value })}
                  rows={2}
                  placeholder="Un errore per riga"
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group" style={{ flex: 2 }}>
                  <label>URL Media</label>
                  <input
                    type="url"
                    value={approvalFormData.media_url}
                    onChange={(e) => setApprovalFormData({ ...approvalFormData, media_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="admin-form-group" style={{ flex: 1 }}>
                  <label>Tipo</label>
                  <select
                    value={approvalFormData.media_type}
                    onChange={(e) => setApprovalFormData({ ...approvalFormData, media_type: e.target.value })}
                  >
                    <option value="gif">GIF</option>
                    <option value="image">Immagine</option>
                    <option value="video">Video</option>
                  </select>
                </div>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setApprovingRequest(null)}>
                  Annulla
                </button>
                <button type="submit" className="admin-btn-primary" disabled={savingApproval}>
                  {savingApproval ? 'Approvazione...' : '✓ Approva e aggiungi al catalogo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Edit Modal */}
      {showUserForm && editingUser && (
        <div className="admin-modal-overlay" onClick={() => setShowUserForm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Modifica Utente</h2>
              <button className="admin-modal-close" onClick={() => setShowUserForm(false)}>×</button>
            </div>
            <form onSubmit={handleUserSubmit} className="admin-modal-form">
              <div className="admin-form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="email@esempio.com"
                />
              </div>

              <div className="admin-form-group">
                <label>Ruolo</label>
                <select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                >
                  <option value="athlete">Atleta</option>
                  <option value="coach">Coach</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowUserForm(false)}>
                  Annulla
                </button>
                <button type="submit" className="admin-btn-primary" disabled={savingUser}>
                  {savingUser ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Exercise Quality Audit component
// ============================================================

const QUALITY_CHECKS = [
  { key: 'description', label: 'Descrizione' },
  { key: 'execution_tips', label: 'Consigli esecuzione' },
  { key: 'common_mistakes', label: 'Errori comuni' },
  { key: 'muscles_involved', label: 'Muscoli coinvolti' },
  { key: 'media_url', label: 'Media (GIF/img)' },
  { key: 'primary_muscle_group', label: 'Gruppo muscolare' },
];

function ExerciseQualityAudit({ exercises, onEdit }) {
  const issues = exercises
    .map(ex => {
      const missing = QUALITY_CHECKS.filter(c => !ex[c.key]).map(c => c.label);
      return missing.length > 0 ? { ...ex, missing } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.missing.length - a.missing.length);

  if (issues.length === 0) {
    return (
      <div className="admin-empty">
        <p>Tutti gli esercizi hanno i campi principali compilati.</p>
      </div>
    );
  }

  return (
    <div className="admin-quality-audit">
      <p className="admin-quality-summary">
        {issues.length} esercizi su {exercises.length} hanno campi mancanti.
      </p>
      <div className="admin-list">
        {issues.map(ex => (
          <div key={ex.id} className="admin-list-item">
            <div className="admin-list-info" style={{ flex: 1 }}>
              <div className="admin-list-name">{ex.name}</div>
              <div className="admin-list-email">{ex.primary_muscle_group || 'Gruppo muscolare mancante'}</div>
              <div className="admin-quality-missing">
                {ex.missing.map(m => (
                  <span key={m} className="admin-quality-tag">{m}</span>
                ))}
              </div>
            </div>
            <button
              className="admin-edit-user-btn"
              onClick={() => onEdit(ex)}
              title="Modifica"
            >
              ✏️
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminDashboard;
